'use no memo';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { DumbClubAvatar } from '@sorare/core/src/components/club/DumbClubAvatar';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import type { RefineListProps } from '@sorare/core/src/components/search/RefineList';
import {
  BackendRefineList,
  RefineList,
} from '@sorare/core/src/components/search/RefineList';
import { GqlFilterTitle } from '@sorare/core/src/contexts/graphql-search';
import { countryCodes } from '@sorare/core/src/hooks/useIntlCountries';
import { sortHitByRarity } from '@sorare/core/src/lib/cards';
import type { Filter, FilterWidget } from '@sorare/core/src/lib/filters';
import { FILTERS } from '@sorare/core/src/lib/filters';
import { transferMarket } from '@sorare/core/src/lib/glossary';
import { formatWord } from '@sorare/core/src/lib/humanize';
import { formatFilterOption as formatPlayerFilterOption } from '@sorare/core/src/lib/players';
import { formatFilter as formatSeasonFilter } from '@sorare/core/src/lib/seasons';
import { flagUrl } from '@sorare/core/src/lib/territories';

import {
  formatPlayingStatus,
  sortPlayingStatus,
} from '#src/lib/playingStatus.ts';
import { RefineListTitle } from '#src/search/RefineListTitle/index.tsx';
import {
  RefineBackendI18nList,
  buildRefineI18nList,
} from '#src/searchCards/RefineI18nList/index.tsx';

export const makeBackendFilter = (
  filter: Filter,
  options: Omit<RefineListProps, 'attribute' | 'visible'> & {
    startsOpen?: boolean;
    hideTitle?: boolean;
  } = {}
): FilterWidget => {
  const {
    startsOpen = false,
    hideTitle,
    sortBy = ['name:asc'],
    ...listProps
  } = options;

  return {
    key: filter.attribute,
    type: 'list',
    attribute: filter.attribute,
    Component: ({ visible }) => (
      <BackendRefineList
        attribute={filter.urlAttribute ?? filter.attribute}
        sortBy={sortBy}
        visible={visible}
        {...listProps}
      />
    ),
    title: !hideTitle && (
      <GqlFilterTitle
        name={filter.urlAttribute ?? filter.attribute}
        title={filter.title}
      />
    ),
    accordionOptions: { startsOpen },
  };
};

export const makeFilter = (
  filter: Filter,
  options: Omit<RefineListProps, 'attribute' | 'visible'> & {
    startsOpen?: boolean;
    hideTitle?: boolean;
  } = {}
): FilterWidget => {
  const {
    startsOpen = false,
    hideTitle,
    sortBy = ['name:asc'],
    ...listProps
  } = options;

  return {
    key: filter.attribute,
    type: 'list',
    attribute: filter.attribute,
    Component: ({ visible }) => (
      <RefineList
        attribute={filter.attribute}
        sortBy={sortBy}
        visible={visible}
        {...listProps}
      />
    ),
    title: !hideTitle && (
      <RefineListTitle attribute={filter.attribute} name={filter.title} />
    ),
    accordionOptions: { startsOpen },
  };
};

export const RefineRarity = makeFilter(FILTERS.rarity, {
  startsOpen: true,
  formatOption: formatWord,
  transformItems: sortHitByRarity,
  renderBefore: option => <ScarcityIcon rarity={option as Rarity} />,
  hideTitle: true,
});

export const RefinePlayer = makeFilter(FILTERS.player, {
  searchable: true,
  formatOption: formatPlayerFilterOption,
  sortBy: ['isRefined:desc', 'name:asc'],
});

const playerSort = ['isRefined:desc' as const, 'name:asc' as const];

export const RefineBackendPlayer = {
  key: 'player',
  type: 'list',
  attribute: 'player',
  Component: ({ visible }: { visible: boolean }) => (
    <BackendRefineList
      searchable
      attribute="player"
      sortBy={playerSort}
      visible={visible}
      formatOption={formatPlayerFilterOption}
    />
  ),
  title: <GqlFilterTitle name="player" title={FILTERS.player.title} />,
  accordionOptions: { startsOpen: false },
} satisfies FilterWidget;

export const RefineSeason = makeFilter(FILTERS.season, {
  formatOption: formatSeasonFilter,
  sortBy: ['name:desc'],
  showMore: true,
});
export const RefineBackendSeason = makeBackendFilter(FILTERS.season, {
  formatOption: formatSeasonFilter,
  sortBy: ['name:desc'],
  showMore: true,
});
export const RefineSingleYearSeason = makeFilter(FILTERS.season, {
  sortBy: ['name:desc'],
  showMore: true,
});

export const RefineCardEdition = makeFilter(FILTERS.cardEdition, {
  searchable: true,
});
export const RefineBackendCardEdition = makeBackendFilter(FILTERS.cardEdition, {
  searchable: true,
});

export const formatBundledOption: NonNullable<
  RefineListProps['formatOption']
> = (bundled, formatMessage) => {
  if (bundled === 'true') return formatMessage(transferMarket.bundles);

  return formatMessage(transferMarket.singleCards);
};
export const RefineBundledSale = makeFilter(FILTERS.bundledSale, {
  formatOption: formatBundledOption,
});

const renderCountryFlag = (label: string) => {
  const [name, slug] = label.split('|');
  return (
    <DumbClubAvatar
      club={{ name, avatarUrl: flagUrl({ country: { slug }, type: 'flat' }) }}
      size={20}
    />
  );
};

export const RefineNationality = buildRefineI18nList({
  filter: FILTERS.nationality,
  renderBefore: renderCountryFlag,
});

export const RefineBackendNationality = {
  key: 'nationality',
  type: 'list',
  attribute: 'nationality',
  Component: ({ visible }) => (
    <RefineBackendI18nList
      visible={visible}
      attribute="nationality"
      renderBefore={renderCountryFlag}
    />
  ),
  title: (
    <GqlFilterTitle name="nationality" title={FILTERS.nationality.title} />
  ),
  accordionOptions: { startsOpen: false },
} satisfies FilterWidget;

export const RefineBirthplace = buildRefineI18nList({
  filter: FILTERS.birthplace,
  renderBefore: renderCountryFlag,
});

export const RefineBackendBirthplace = {
  key: 'birthplace',
  type: 'list',
  attribute: 'nationality',
  Component: ({ visible }) => (
    <RefineBackendI18nList
      visible={visible}
      attribute="nationality"
      renderBefore={renderCountryFlag}
    />
  ),
  title: <GqlFilterTitle name="nationality" title={FILTERS.birthplace.title} />,
  accordionOptions: { startsOpen: false },
} satisfies FilterWidget;
export const RefineActiveNationalTeam = buildRefineI18nList({
  filter: FILTERS.activeNationalTeam,
  renderBefore: label => {
    const [name, enName] = label.split('|');
    const cleanEnName = enName.replace(/\sUnder \d+/, '');

    return renderCountryFlag(`${name}|${countryCodes[cleanEnName]}`);
  },
});

const renderCountryFlagBefore = (label: string) => {
  const [name, enName] = label.split('|');
  const cleanEnName = enName.replace(/\sUnder \d+/, '');

  return renderCountryFlag(`${name}|${countryCodes[cleanEnName]}`);
};
export const RefineBackendActiveNationalTeam = {
  key: 'nt',
  type: 'list',
  attribute: 'nt',
  Component: ({ visible }) => (
    <RefineBackendI18nList
      visible={visible}
      attribute="nt"
      renderBefore={renderCountryFlagBefore}
    />
  ),
  title: <GqlFilterTitle name="nt" title={FILTERS.activeNationalTeam.title} />,
  accordionOptions: { startsOpen: false },
} satisfies FilterWidget;

const renderLeagueLogo = (label: string) => {
  const [name, slug] = label.split('|');
  return (
    <LeagueAvatar
      league={{
        name,
        slug,
      }}
      size={24}
    />
  );
};

export const RefineActiveLeague = ({
  sortBy,
  ...rest
}: Partial<Omit<Parameters<typeof buildRefineI18nList>[0], 'filter'>> = {}) =>
  buildRefineI18nList({
    filter: FILTERS.activeLeague,
    sortBy: sortBy ?? ['isRefined:desc', 'count:desc', 'name:asc'],
    renderBefore: renderLeagueLogo,
    ...rest,
  });

export const RefineBackendActiveLeague = ({
  filterBy,
  showMore,
  searchable,
}: {
  filterBy?: (item: any) => boolean;
  showMore?: boolean;
  searchable?: boolean;
} = {}) => {
  return {
    key: 'al',
    type: 'list',
    attribute: 'al',
    Component: ({ visible }) => (
      <RefineBackendI18nList
        visible={visible}
        attribute="al"
        renderBefore={renderLeagueLogo}
        filterBy={filterBy}
        showMore={showMore}
        searchable={searchable}
      />
    ),
    title: <GqlFilterTitle name="al" title={FILTERS.activeLeague.title} />,
    accordionOptions: { startsOpen: false },
  } satisfies FilterWidget;
};

export const RefinePlayingStatus = makeFilter(FILTERS.playingStatus, {
  formatOption: formatPlayingStatus,
  transformItems: sortPlayingStatus,
});

export const RefineBackendPlayingStatus = makeBackendFilter(
  FILTERS.playingStatus,
  {
    formatOption: formatPlayingStatus,
    transformItems: sortPlayingStatus,
  }
);
